import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { LayoutDashboard, ScanLine, Users, User, LogOut, FileText, BookMarked, Briefcase, Settings as SettingsIcon, CreditCard, LineChart, Shield, Receipt, Gift, Scale, Radio, AlertTriangle, Sparkles, BarChart3, Gem, BookOpen, Newspaper, Gamepad2, Crown } from "lucide-react";
import { useAuth } from "@/App";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import ProcessosSidebar from "@/components/ProcessosSidebar";

const items = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, test: "nav-dashboard" },
  { to: "/noticias", label: "Notícias", icon: Newspaper, test: "nav-noticias", accent: true },
  { to: "/reader", label: "Ler Documentos", icon: ScanLine, test: "nav-reader" },
  { to: "/clients", label: "Clientes", icon: Users, test: "nav-clients" },
  { to: "/kanban", label: "Escritório", icon: Briefcase, test: "nav-office" },
  { to: "/djen", label: "DJEN", icon: Radio, test: "nav-djen", danger: true },
  { to: "/prazos", label: "Prazos", icon: AlertTriangle, test: "nav-prazos" },
  { to: "/preparados", label: "Preparados", icon: Sparkles, test: "nav-preparados", accent: true },
  { to: "/vale-a-pena", label: "Vale a Pena?", icon: BarChart3, test: "nav-valepena", accent: true },
  { to: "/jurisprudencia", label: "Jurisprudência", icon: BookOpen, test: "nav-jurisprudencia", accent: true },
  { to: "/mineral", label: "Hermes Mineral", icon: Gem, test: "nav-mineral", accent: true },
  { to: "/inteligencia", label: "Inteligência 360", icon: Sparkles, test: "nav-intel360", accent: true },
  { to: "/calculos", label: "Atualização de Cálculos", icon: Scale, test: "nav-calculos" },
  { to: "/finance", label: "Financeiro", icon: LineChart, test: "nav-finance" },
  { to: "/indicacoes", label: "Indicações", icon: Gift, test: "nav-indicacoes" },
  { to: "/templates", label: "Modelos & Cláusulas", icon: BookMarked, test: "nav-templates" },
  { to: "/jogos", label: "Jogos", icon: Gamepad2, test: "nav-jogos", accent: true },
  { to: "/plans", label: "Planos", icon: CreditCard, test: "nav-plans" },
  { to: "/meu-plano", label: "Meu Plano", icon: Crown, test: "nav-my-plan" },
  { to: "/settings", label: "Configurações", icon: SettingsIcon, test: "nav-settings" },
  { to: "/profile", label: "Meu Perfil", icon: User, test: "nav-profile" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  // Mini indicador CNJ ao lado do link DJEN — status ao vivo (poll leve 60s)
  const [djenHealth, setDjenHealth] = useState(null);
  const prevUnreadRef = useRef(null);
  useEffect(() => {
    let mounted = true;
    const navigateToDjen = () => { window.location.assign("/djen"); };
    const load = () => api.get("/djen/status")
      .then(r => {
        if (!mounted) return;
        setDjenHealth(r.data?.health || null);
        const unread = r.data?.unread_count ?? 0;
        // Toast on unread_count increase (skip first load)
        if (prevUnreadRef.current != null && unread > prevUnreadRef.current) {
          const delta = unread - prevUnreadRef.current;
          toast(`${delta} nova${delta > 1 ? "s" : ""} publicação${delta > 1 ? "ões" : ""} DJEN`, {
            description: "Fonte oficial CNJ · sincronizada agora.",
            duration: 8000,
            action: { label: "Abrir", onClick: navigateToDjen },
          });
        }
        prevUnreadRef.current = unread;
      })
      .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(t); };
  }, []);
  const djenDot = {
    CONNECTED: "bg-emerald-500",
    DEGRADED: "bg-amber-500",
    UNAVAILABLE: "bg-slate-400",
    UNKNOWN: "bg-slate-300",
  }[djenHealth] || "bg-slate-300";
  const djenTitle = {
    CONNECTED: "CNJ conectado",
    DEGRADED: "CNJ com histórico (temporariamente indisponível)",
    UNAVAILABLE: "CNJ temporariamente indisponível",
    UNKNOWN: "CNJ aguardando primeira sincronização",
  }[djenHealth] || "CNJ status desconhecido";
  return (
    <aside className="w-64 shrink-0 bg-[#0E1D2D] border-r border-black/20 flex flex-col min-h-screen text-slate-100">
      <div className="px-6 py-6 border-b border-white/5">
        <Logo size="md" tone="dark" showSub={false} />
        <div className="mt-2 text-[11px] text-slate-400 leading-tight font-serif italic">
          Mais tempo para o que<br/>realmente importa.
        </div>
      </div>
      <nav className="p-3 space-y-0.5 flex-1 overflow-y-auto">
        {items.map(({ to, label, icon: I, test, accent, danger }) => (
          <NavLink
            key={to} to={to} data-testid={test}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition ${
                isActive
                  ? "bg-gradient-to-r from-[#C9953D] to-[#D2A85A] text-[#0E1D2D] shadow-md shadow-black/20"
                  : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <I className="w-4 h-4" />
            <span className="flex-1">{label}</span>
            {to === "/djen" && djenHealth && (
              <span data-testid="nav-djen-status-dot" title={djenTitle}
                className={`w-2 h-2 rounded-full ${djenDot} shrink-0`}/>
            )}
          </NavLink>
        ))}
        {user?.is_owner && (
          <NavLink to="/admin" data-testid="nav-admin"
            className={({ isActive }) =>
              `mt-4 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition border ${
                isActive
                  ? "bg-[#0b0f1a] text-[#d4a24c] border-[#d4a24c]"
                  : "bg-[#0b0f1a]/95 text-[#d4a24c] border-[#3a2a15] hover:border-[#d4a24c]"
              }`}
          >
            <Shield className="w-4 h-4" /> Admin · OWNER
          </NavLink>
        )}
        <ProcessosSidebar />
      </nav>
      <div className="p-3 border-t border-white/5 space-y-2">
        <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-3">
          <div className="flex items-start gap-2">
            <span className="text-[#D2A85A] mt-0.5">✦</span>
            <div className="text-[11px] text-slate-300 font-serif italic leading-snug">
              Você advoga.<br/>Nós organizamos o caminho.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 px-2 py-1">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-9 h-9 rounded-full" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-slate-200 text-sm">
              {user?.name?.[0] || "A"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-100 truncate">{user?.name}</div>
            <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
          </div>
        </div>
        <button
          data-testid="btn-logout"
          onClick={logout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-[13px] text-slate-400 hover:bg-white/5 hover:text-white transition"
        >
          <LogOut className="w-4 h-4" /> Sair
        </button>
      </div>
    </aside>
  );
}
